import { useState, useCallback } from 'react';
import { apiPost, ApiResponse, ApiError } from '../../services/api.service';

interface Group {
  id: number;
  name: string;
  description?: string;
  group_code: number;
  creator_id: number;
  created_at: string;
  updated_at: string;
}

interface CreateGroupData {
  name?: string;
  description?: string;
}

interface UseCreateGroupReturn {
  createGroup: (data: CreateGroupData) => Promise<Group | null>;
  loading: boolean;
  error: string | null;
  success: boolean;
}

/**
 * Custom hook to create a new group
 * @returns Function to create group, loading state, error state, and success state
 */
export function useCreateGroup(): UseCreateGroupReturn {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const createGroup = useCallback(
    async (data: CreateGroupData): Promise<Group | null> => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(false);

        const response: ApiResponse<Group> = await apiPost<Group>(
          '/groups',
          data,
          {
            notification: {
              enabled: true,
              successMessage: 'Gruppe erfolgreich erstellt!',
              errorMessage: 'Fehler beim Erstellen der Gruppe. Bitte versuchen Sie es erneut.',
            },
          }
        );

        if (response.success && response.data) {
          setSuccess(true);
          return response.data;
        } else {
          setError(response.error || 'Fehler beim Erstellen der Gruppe');
          return null;
        }
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Ein unerwarteter Fehler ist aufgetreten');
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    createGroup,
    loading,
    error,
    success,
  };
}